import { money } from '@/components/estimating/assemblies/assemblyFormat';

export { money };
export { dateTime } from '@/components/estimating/estimates/estimateFormat';

export const BUCKETS = [
  { key: 'material', label: 'Materials' },
  { key: 'labor', label: 'Labor' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'subcontractor', label: 'Subcontractors' },
  { key: 'other', label: 'Other' },
];
export const BUCKET_LABEL = Object.fromEntries(BUCKETS.map((b) => [b.key, b.label]));
export const BUCKET_OPTIONS = BUCKETS.map((b) => ({ value: b.key, label: b.label }));
export const SOURCE_LABEL = { manual: 'Manual', pulse: 'Pulse', po: 'PO', receipt: 'Receipt', equipment: 'Equipment use' };
// Budget vs actual in the theme's status colors (strata-theme.css), judged by the server.
export const STATUS = {
  ok: { color: 'var(--strata-success)', label: 'On budget' },
  near: { color: 'var(--strata-warning)', label: 'Near budget' },
  over: { color: 'var(--strata-danger)', label: 'Over budget' },
  unbudgeted: { color: 'var(--strata-warning)', label: 'No budget' },
  none: { color: 'var(--strata-text-muted)', label: 'No costs yet' },
  loss: { color: 'var(--strata-danger)', label: 'Projected loss' },
};
export const MAX_ENTRY = 1000000;
export const MAX_BUDGET = 100000000;

export const signed = (n) => (Number(n) < 0 ? `−${money(-Number(n))}` : money(n));
export const pct1 = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
export const cents = (c) => signed(Number(c || 0) / 100);
// The solid (spent) part of a budget gauge whose full fill is the projected total (Loop 9); the rest is committed.
export const actualPct = (b) => (b.budget > 0 && b.committed > 0 ? Math.max(0, (Number(b.actual) * 100) / Number(b.budget)) : null);

// What was typed, as the exact string the server rounds (half-up, to the cent). Never a float round-trip.
export function parseAmount(raw, { negative = true, zero = false, max = MAX_ENTRY } = {}) {
  const s = String(raw ?? '').replace(/[\s$,]/g, '').replace('−', '-');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return { error: 'Enter an amount, e.g. 42.50.' };
  const v = Number(s);
  if (v < 0 && !negative) return { error: 'Enter an amount of $0 or more.' };
  if (!zero && Math.round(Math.abs(v) * 100) === 0) return { error: 'Enter an amount other than $0.00.' };
  if (Math.abs(v) > max) return { error: `Enter an amount up to ${money(max)}.` };
  return { value: s };
}

// "2026-06-12" → "Jun 12, 2026", read as a calendar day (never shifted by a timezone).
export const dayLabel = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
export const listKey = (e) => `${e.date}|${e.created_at}|${e.id}`;
export const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`);
export const isStale = (e) => e?.response?.status === 409 && !!e.response.data?.stale;
export const detail = (e, fallback) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);
export const hasReceipt = (e) => !!e.receipt && !e.receipt.removed;
// Pulse labor (Loop 8): hours are hundredths, rates are cents per hour.
export const hrs = (x100) => `${(Number(x100 || 0) / 100).toFixed(2)} h`;
export const perHour = (c) => `${money(Number(c || 0) / 100)}/h`;
export const seatLabel = (s) => (s ? `${s.seat_name} · ${s.crew_name}` : '');
export const toCents = (s) => Math.round(Number(s) * 100);
// Typed hours (Loop 9.5): "8.5" → the exact string the server rounds to hundredths. A cost's hours are above 0.
export function parseHours(raw, { zero = false, max = 10000 } = {}) {
  const s = String(raw ?? '').replace(/[\s,h]/gi, '');
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return { error: 'Enter hours, e.g. 8.5.' };
  const v = Number(s);
  if ((!zero && Math.round(v * 100) === 0) || v > max) return { error: `Enter hours ${zero ? 'from 0' : 'above 0'} up to ${max.toLocaleString()}.` };
  return { value: s };
}
export const hoursValue = (x100) => (Number(x100 || 0) / 100).toFixed(2);
// POs + receipts (Loop 9): a receipt's cost needs a price, was read by OCR (a person approves it), or is priced.
export const PRICE_STATE = { needs_price: 'Needs a price', pending_approval: 'Read by OCR · approve', approved: 'Priced' };
export const AMBER_CHIP = { color: '#7A5A12', background: 'color-mix(in srgb, var(--strata-warning) 22%, transparent)', borderColor: 'var(--strata-warning)' };
export const CHIP = 'inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide';
// A Ledger receipt in the shape the Files URL helpers read (thumb / preview / original, signed delivery).
export const fileOf = (r) => ({ id: r.file_id, content_type: r.content_type, original_filename: r.filename, is_image: r.is_image });

// An OCR read → what to fill in the entry strip: only empty fields (and the date only while it's still today).
export function fillFrom(s, d, today) {
  const patch = {};
  const filled = [];
  if (s.amount && !d.amount.trim()) {
    const credit = s.amount.startsWith('-');
    Object.assign(patch, { amount: credit ? s.amount.slice(1) : s.amount, credit });
    filled.push(signed(s.amount));
  }
  if (s.vendor && !d.note.trim()) { patch.note = s.vendor; filled.push(s.vendor); }
  if (s.date && d.day === today && s.date !== today) { patch.day = s.date; filled.push(dayLabel(s.date)); }
  return { patch, filled };
}
